import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useActiveShow } from "common-fe";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { ringsFor, resolveDefaultRing, availableRingsFor } from "../utils/rings";

const STORAGE_KEY = "announcer-fe.announcerContext";

const AnnouncerContext = createContext(null);

function loadInitialRing() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.selectedRing ?? null;
  } catch {
    return null;
  }
}

function persistRing(selectedRing) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedRing }));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

// #2112 — announcer moved off the kiosk shell: org and show are now the
// shared ActiveOrgProvider / ActiveShowProvider selections (App.jsx), the
// same pattern scoreadmin-fe uses (AdminShell's OrgSwitcher/ShowSwitcher),
// switched freely from the app bar with no PIN gate. This context no longer
// owns show selection at all — it reads the shared active show and keeps
// only the RING dimension, which was already an ungated, PIN-free switch
// before this card (#926), plus the ring-resolution machinery that has
// always lived here. #2190 removed the day-prefetch machinery that used to
// live here too — no local caching remains.
export function AnnouncerProvider({ children }) {
  const { activeShow: selectedShow } = useActiveShow();
  const [selectedRing, setSelectedRingState] = useState(loadInitialRing);

  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  // #912 — NEVER gate DATA READS on raw navigator.onLine. On flaky venue wifi
  // Chrome flips navigator.onLine to false and frequently never fires 'online'
  // again, which silently suppressed status badges + placings at a LIVE show.
  // Always attempt fetches: #2190 — every read hook now leaves whatever's
  // already on screen alone on a real failure (in-memory only, no local
  // cache), and the "not live" banner is driven off actual data staleness +
  // the raw browser flag (browserOnline below), not off gating reads.
  const online = true;

  // #1740 — ring is no longer a caller-supplied argument when a show is picked: the
  // arena tabs (OrderOfGo, #1646) are the only place ring is chosen now, so
  // the context resolves its OWN default the moment a show is set, from the
  // same source ringsFor(classList) OrderOfGo's tab row uses (score-api has no
  // ring-listing route). This fetch is deliberately separate from OrderOfGo's
  // own polling useClassesForShow call — it exists purely to unblock the
  // default-ring resolution below without waiting on /oog to mount.
  const { data: classList, loading: classListLoading } = useClassesForShow(
    selectedShow?.id ?? null,
  );
  // #1909 — originally preferred the show's per-show summary row (rings in
  // ringsFor's own order, written by reconcile) over reducing the class list
  // ourselves, on the theory that summary.rings was at least as fresh as the
  // rest of the persisted session.
  // #1968 — that theory was wrong: unlike selectedShow's other fields,
  // summary is NEVER refreshed after a show is selected (see #1968's card
  // description), so on a kiosk that stays on one show for days, a ring
  // added mid-show (re-ingest) was invisible to availableRings until the
  // operator re-selected the show (at the time, via the PIN-gated setup
  // screen this card has since removed) — silently snapping a reload
  // off a ring the operator had just picked (#1968 AC1/AC2). Chosen fix
  // (approach 2 of the card's options): summary.rings is now only an
  // EARLY-PAINT SEED for the brief window before the live class list (this
  // hook's own fetch, same source ringsFor(classList) always used — NOT
  // polled like OrderOfGo's own instance, just re-run on mount / showId
  // change, which a reload triggers) has produced anything — liveRings wins
  // the instant it has any rings at all, so a stale summary can never shadow
  // one the class list already knows about. See availableRingsFor's own
  // header (utils/rings.js) for the merge rule itself.
  const liveRings = useMemo(() => ringsFor(classList), [classList]);
  const availableRings = useMemo(
    () => availableRingsFor(liveRings, selectedShow?.summary?.rings),
    [liveRings, selectedShow?.summary],
  );

  // Resolve the session's ring ONCE per show, the moment its class list is
  // known. Keep the persisted ring if it's still one of this show's rings;
  // otherwise fall back to the first entry (natural-numeric order, ringsFor).
  // Bails out (does nothing) while classes haven't loaded yet — #1740 AC3
  // requires /oog to render, not bounce, for a show whose rings aren't known
  // yet, and this effect must not clobber a still-valid persisted ring with
  // nothing while that fetch is in flight; it retries on the next class-list
  // update rather than giving up.
  //
  // resolvedForShowRef (not state) is what makes this a ONE-SHOT per show
  // rather than a continuous invariant: `selectedRing` stays a dependency
  // (needed to react the first time availableRings arrives after the show is
  // set) but once resolvedForShowRef.current === showId, the effect returns
  // immediately no matter how selectedRing or availableRings change after
  // that. Without this guard, adding a ring mid-show (re-ingest, #692-style)
  // and then tapping ITS tab would re-run resolveDefaultRing against a
  // frozen availableRings that doesn't include the new ring yet, snap the
  // operator's explicit choice back to the old default with no error, and
  // strand a kiosk nobody can reload — the exact dead-end this card exists to
  // remove, relocated from /setup to /oog. switchRing (below) owns the ring
  // for the rest of the session once this has resolved once. Also re-arms on
  // an org/show SWITCH (#2112 — now free, from the app bar's ShowSwitcher,
  // not just a reload): the new selectedShow.id simply won't match whatever
  // id is already in the ref, same as the reload case always worked.
  const resolvedForShowRef = useRef(null);
  // Exposed purely for test observability (#2190 judge remedy): a plain
  // boolean derived from comparing this to selectedShow?.id, so a Cypress
  // spec can wait for the one-shot resolution to have actually run for the
  // CURRENT show, instead of a retrying `.should()` that can pass vacuously
  // during the render commit before this effect has fired even once (ring
  // tabs paint off the persisted selectedRing value immediately, well
  // before this effect gets a chance to run). Read-only signal, changes no
  // resolution behavior above.
  const [ringResolvedShowId, setRingResolvedShowId] = useState(null);
  useEffect(() => {
    const showId = selectedShow?.id;
    if (!showId || resolvedForShowRef.current === showId) return;
    // #1968 — wait for the live class list's OWN first fetch to settle before
    // trusting availableRings for this one-shot. Without this, a reload with
    // a stale summary.rings (and no live rings yet, still loading) would
    // resolve immediately off the stale set and permanently lock
    // resolvedForShowRef before the fresher class list ever arrived —
    // reintroducing the exact frozen-snapshot bug this card exists to fix,
    // just moved one effect over. classListLoading always settles to false
    // (useRemoteResource resolves loading once its first fetch settles,
    // success or failure), so this can't block forever.
    if (classListLoading && !liveRings.length) return;
    const resolved = resolveDefaultRing(selectedRing, availableRings);
    if (!resolved) return; // rings not known yet - retry on the next class-list update
    resolvedForShowRef.current = showId;
    setRingResolvedShowId(showId);
    if (resolved !== selectedRing) setSelectedRingState(resolved);
  }, [selectedShow?.id, availableRings, selectedRing, classListLoading, liveRings.length]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => setBrowserOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    persistRing(selectedRing);
  }, [selectedRing]);

  // #926 — ring switching has always been ungated (a pill/tab row in
  // OrderOfGo.jsx calls this directly); nothing here changes with #2112.
  const switchRing = useCallback((ring) => {
    setSelectedRingState(ring);
  }, []);

  const ringResolved = ringResolvedShowId === selectedShow?.id;

  // #2140 (docs/score/announcer-ui-redesign.md §11) — VIEWING is cross-band
  // (the day spine sets it on a class row click; #2142's look-up column
  // reads it to know what to show), so it lives here rather than in either
  // band's own component. Null means "following the ring" — RUNNING is what
  // paints when nothing has been explicitly pinned. This is genuinely
  // distinct from RUNNING: RUNNING moves on its own as the show advances,
  // VIEWING moves ONLY on a click.
  const [viewingClassId, setViewingClassId] = useState(null);
  // Selecting a ring resets look-up state (doc §11): a class id from the OLD
  // ring pinned in VIEWING would be meaningless (or worse, coincide with a
  // different class) once the ring switches. Ref-compare against the
  // PREVIOUS ring, same one-shot-per-change idiom resolvedForShowRef already
  // uses above, so this doesn't fire on every render, only on an actual
  // switch.
  const previousRingRef = useRef(selectedRing);
  useEffect(() => {
    if (previousRingRef.current === selectedRing) return;
    previousRingRef.current = selectedRing;
    setViewingClassId(null);
  }, [selectedRing]);

  const value = useMemo(
    () => ({
      selectedShow,
      selectedRing,
      online,
      browserOnline,
      switchRing,
      ringResolved,
      viewingClassId,
      setViewingClassId,
    }),
    [
      selectedShow,
      selectedRing,
      online,
      browserOnline,
      switchRing,
      ringResolved,
      viewingClassId,
    ],
  );

  return (
    <AnnouncerContext.Provider value={value}>{children}</AnnouncerContext.Provider>
  );
}

export function useAnnouncerContext() {
  const ctx = useContext(AnnouncerContext);
  if (!ctx) {
    throw new Error("useAnnouncerContext must be used within an AnnouncerProvider");
  }
  return ctx;
}
